import { FieldValue, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  COMMUNITY_NAME,
  COMMUNITY_REGISTERED_NAME,
  LANGUAGE,
  UNITS,
  ADDRESS_1,
  ADDRESS_2,
  ZIPCODE,
  STATE,
  CITY,
  GENERAL_PHONE,
  EMERGENCY_PHONE,
  MANAGER_NAME,
  MANAGER_PHONE,
  CONTACT_EMAIL,
  WEBSITE,
  COUNTY,
  AUDIT_SCOPE,
  AUDIT_STATE,
  LICENSE_CATEGORY,
  ACCOUNTING_METHOD,
} from "./consts";
import {
  useAddCommunityInfoMutation,
  useGetCommunityReview,
  useGetUserById,
  useGetUsersList,
  useUpdateCommunityInfoMutation,
} from "@/services";
import { toast } from "@/components/Toast";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { routes } from "@/routes/utils";
import { getRequiredFields, removeEmptyFields, setEmptyFieldsToNull } from "@/lib/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { communityFormValues, communitySchema } from "./validations";

const useStep1 = ({ onNextClick }: { onNextClick?: () => void }) => {
  const navigate = useNavigate();
  const { id, action } = useParams();
  const isEditDetails = action === "edit-details";
  const [keys, setKeys] = useState<any>(
    {
      stateKey: Math.random() * 3,
      cityKey: Math.random() * 3,
      scopeKey: Math.random() * 3,
      managerKey: Math.random() * 3,
      licenseCatKey: Math.random() * 3,
      accMethodKey: Math.random() * 3
    }
  )
  const stateList = useSelector(
    (state: RootState) => state.currentData.stateData
  );

  const form = useForm<communityFormValues>({
    defaultValues: {
      [COMMUNITY_NAME]: "",
      [COMMUNITY_REGISTERED_NAME]: undefined,
      [LANGUAGE]: "EN",
      [UNITS]: "",
      [ADDRESS_1]: "",
      [ADDRESS_2]: "",
      [ZIPCODE]: "",
      [STATE]: "",
      [AUDIT_STATE]: "",
      [CITY]: "",
      [GENERAL_PHONE]: undefined,
      [EMERGENCY_PHONE]: "",
      [MANAGER_NAME]: "",
      [MANAGER_PHONE]: undefined,
      [CONTACT_EMAIL]: "",
      [WEBSITE]: undefined,
      [COUNTY]: "",
      [AUDIT_SCOPE]: "",
      [LICENSE_CATEGORY]: "",
      [ACCOUNTING_METHOD]: ""
    },
    resolver: zodResolver(communitySchema),
  });

  // Dynamically determine required fields
  const requiredFields = getRequiredFields(communitySchema);

  const {
    data: communityDetail,
    isLoading: isDetailLoading,
    isRefetching: isDetailRefetching,
  } = useGetCommunityReview({
    community_id: id || "",
    queryParams: {
      enabled: !!id,
    },
  });

  useEffect(() => {
    if (communityDetail) {
      form.reset({
        [COMMUNITY_NAME]: communityDetail?.data?.[COMMUNITY_NAME] || "",
        [COMMUNITY_REGISTERED_NAME]:
          communityDetail?.data?.[COMMUNITY_REGISTERED_NAME] || undefined,
        [LANGUAGE]: communityDetail?.data?.[LANGUAGE] || "EN",
        [UNITS]: communityDetail?.data?.[UNITS]?.toString() || "",
        [ADDRESS_1]: communityDetail?.data?.[ADDRESS_1] || "",
        [ADDRESS_2]: communityDetail?.data?.[ADDRESS_2] || "",
        [ZIPCODE]: communityDetail?.data?.[ZIPCODE] || "",
        [STATE]:
          stateList?.find(
            (state: { name: string; code: string }) =>
              state.name == communityDetail?.data?.[STATE]?.toString() ||
              state.code == communityDetail?.data?.[STATE]?.toString()
          )?.code || "",
        [CITY]: communityDetail?.data?.[CITY]?.toString() || "",
        [GENERAL_PHONE]: communityDetail?.data?.[GENERAL_PHONE] || undefined,
        [EMERGENCY_PHONE]: communityDetail?.data?.[EMERGENCY_PHONE] || "",
        [MANAGER_NAME]: communityDetail?.data?.[MANAGER_NAME]?.toString() || "",
        [MANAGER_PHONE]: communityDetail?.data?.[MANAGER_PHONE] || undefined,
        [CONTACT_EMAIL]: communityDetail?.data?.[CONTACT_EMAIL] || "",
        [WEBSITE]: communityDetail?.data?.[WEBSITE] || undefined,
        [COUNTY]: communityDetail?.data?.[COUNTY] || "",
        [AUDIT_SCOPE]: communityDetail?.data?.[AUDIT_SCOPE] || "",
        [AUDIT_STATE]: stateList?.find(
          (state: { name: string; code: string }) =>
            state.name == communityDetail?.data?.[AUDIT_STATE]?.toString() ||
            state.code == communityDetail?.data?.[AUDIT_STATE]?.toString()
        )?.code || "",
        [LICENSE_CATEGORY]: communityDetail?.data?.[LICENSE_CATEGORY] || "",
        [ACCOUNTING_METHOD]: communityDetail?.data?.[ACCOUNTING_METHOD] || ""
      });
      setKeys((prev: any) => ({
        ...prev,
        stateKey: Math.random() * 3,
        cityKey: Math.random() * 3,
        scopeKey: Math.random() * 3,
        licenseCatKey: Math.random() * 3,
        accMethodKey: Math.random() * 3,
      }));
    }
  }, [communityDetail, stateList]);

  const { data: managerList, isLoading: isManagerLoading } = useGetUsersList({
    data: {
      filters: [
        {
          field: "role",
          value: "ROLE_EMPLOYEE",
          operator: "EQUAL",
        },
      ],
      pageNumber: 0,
      pageSize: 1000,
    },
  });

  useEffect(() => {
    if (managerList) {
      setKeys((prev: any) => ({
        ...prev,
        managerKey: Math.random() * 3,
      }));
    }
  }, [managerList]);

  const { data: userDetail, isLoading: isLoadingUser } = useGetUserById({
    id: form.watch(MANAGER_NAME) as string,
    queryParams: {
      enabled: !!form.watch(MANAGER_NAME),
    },
  });

  const { mutate: addInfo, isPending: isAddPending } =
    useAddCommunityInfoMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: (data) => {
        if (isEditDetails) {
          navigate(
            `/${routes.cam}/${routes.communities}/${data.data.data}/${routes.details}`
          );
        } else {
          navigate(
            `/${routes.cam}/${routes.communities}/${data.data.data}/${action}`,
            {
              state: { step: 2 },
            }
          );
        }
        toast.success("Community Info Added Successfully");
      },
    });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateCommunityInfoMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        if (isEditDetails) {
          navigate(
            `/${routes.cam}/${routes.communities}/${id}/${routes.details}`
          );
        } else {
          onNextClick && onNextClick();
        }
        toast.success("Community Info Updated Successfully");
      },
    });

  useEffect(() => {
    if (userDetail) {
      form.setValue(MANAGER_PHONE, userDetail?.data?.phoneNumber);
    }
  }, [userDetail]);

  const onSubmit = (data: FieldValue<any>) => {
    delete data[MANAGER_PHONE];
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);
    if (id) {
      updateInfo({
        id: id || "",
        data: {
          ..._data,
          [STATE]: stateList.find((state: any) => state.code == _data[STATE])
            ?.name,
          [AUDIT_STATE]: stateList.find((state: any) =>
            state.code == _data[AUDIT_STATE] || state.name == _data[AUDIT_STATE]
          )?.name || null,
        },
      });
    } else {
      addInfo({
        data: {
          ..._data,
          [STATE]: stateList.find((state: any) => state.code == _data[STATE])
            ?.name,
          [AUDIT_STATE]: stateList.find((state: any) =>
            state.code == _data[AUDIT_STATE] || state.name == _data[AUDIT_STATE]
          )?.name || null,
        },
      });
    }
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isAddPending,
    stateList,
    managerList,
    isManagerLoading,
    isUpdatePending,
    isEditDetails,
    isDetailLoading,
    isDetailRefetching,
    keys,
    isLoadingUser,
  };
};

export default useStep1;
